/**
 * Valhalla routing client.
 * 
 * Wraps the existing Cloudflare proxy at /api/route which sits in front of
 * the self-hosted Valhalla instance at routing.pluginany.com.
 * 
 * Reasons for the proxy:
 *  - Same-origin requests (iOS Safari CORS issue)
 *  - Centralised rate limiting and logging
 *  - Hides VPS IP from clients
 */

const PROXY = import.meta.env.VITE_VALHALLA_PROXY || 'https://pluginany.com/api/route';

/**
 * Plan a route between two points.
 * 
 * @param {{lat, lng}} origin
 * @param {{lat, lng}} destination  
 * @param {Object} options
 * @returns {Promise<{
 *   geometry_simplified: Array<[lng, lat]>,
 *   segments: Array<{
 *     distance_km, avg_speed_kmh, gradient_pct, segment_bearing_deg, altitude_m
 *   }>,
 *   total_distance_km, total_drive_time_min
 * }>}
 */
export async function planRoute(origin, destination, options = {}) {
  const {
    costing = 'auto',
    elevation = true,
    units = 'kilometers',
    waypoints = []
  } = options;

  const locations = [
    { lat: origin.lat, lon: origin.lng },
    ...waypoints.map(w => ({ lat: w.lat, lon: w.lng })),
    { lat: destination.lat, lon: destination.lng }
  ];

  const payload = {
    locations,
    costing,
    directions_options: { units },
    elevation_interval: elevation ? 50 : 0,  // meters
    shape_match: 'walk_or_snap'
  };

  const resp = await fetch(PROXY, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });

  if (!resp.ok) {
    throw new Error(`Valhalla routing failed: ${resp.status} ${resp.statusText}`);
  }
  const data = await resp.json();
  return processValhallaResponse(data);
}

/**
 * Convert raw Valhalla response into the simplified segment format the
 * range engine expects.
 */
function processValhallaResponse(raw) {
  // Decode the polyline-6 encoded shape
  const points = decodePolyline6(raw.trip.legs[0].shape);
  
  // Simplify to <= 200 points for mobile rendering
  const simplified = simplifyPath(points, 200);
  
  // Build segments — each ~5km
  const segments = [];
  const targetSegmentKm = 5;
  let segStart = 0;
  
  // Approximate cumulative distance per point
  let cumulative = [0];
  for (let i = 1; i < points.length; i++) {
    cumulative.push(cumulative[i-1] + haversine(points[i-1], points[i]));
  }
  
  const totalKm = cumulative[cumulative.length - 1];
  const elevation = raw.trip.legs[0].elevation || [];
  
  let cursorIdx = 0;
  while (cursorIdx < points.length - 1) {
    const startKm = cumulative[cursorIdx];
    let endIdx = cursorIdx;
    while (endIdx < points.length - 1 && cumulative[endIdx] - startKm < targetSegmentKm) {
      endIdx++;
    }
    
    const distance_km = cumulative[endIdx] - startKm;
    if (distance_km < 0.1) { cursorIdx = endIdx + 1; continue; }
    
    // Bearing from start to end
    const bearing = computeBearing(points[cursorIdx], points[endIdx]);
    
    // Elevation
    const elev_start = elevation[cursorIdx] ?? null;
    const elev_end = elevation[endIdx] ?? null;
    const gradient_pct = (elev_start != null && elev_end != null && distance_km > 0)
      ? ((elev_end - elev_start) / (distance_km * 1000)) * 100
      : 0;
    
    segments.push({
      distance_km,
      avg_speed_kmh: estimateSegmentSpeed(points[cursorIdx], raw),
      gradient_pct,
      segment_bearing_deg: bearing,
      altitude_m: elev_start || 0,
      lat: points[cursorIdx][1],
      lng: points[cursorIdx][0]
    });
    
    cursorIdx = endIdx + 1;
  }
  
  return {
    geometry_simplified: simplified,
    geometry_full: points,        // full [lng,lat] path — needed for nav position-snapping
    segments,
    total_distance_km: totalKm,
    total_drive_time_min: raw.trip.summary?.time ? raw.trip.summary.time / 60 : null,
    maneuvers: extractManeuvers(raw),
    raw_response: raw
  };
}

/**
 * Extract turn-by-turn maneuvers from a Valhalla response into a compact
 * shape the UI can render directly. Each item:
 *   { instruction, distance_km, time_min, type, street,
 *     begin_shape_index, end_shape_index }
 *
 * `type` is Valhalla's numeric maneuver code — the DirectionsList component
 * maps it to an icon.
 *
 * `begin_shape_index` / `end_shape_index` are offsets into the route's
 * decoded shape polyline. Navigation uses these to know WHERE along the
 * route each maneuver happens, so it can advance the active step as the
 * driver's GPS position moves along the path.
 */
function extractManeuvers(raw) {
  const legs = raw?.trip?.legs;
  if (!Array.isArray(legs) || legs.length === 0) return [];
  const out = [];
  for (const leg of legs) {
    const mans = leg?.maneuvers;
    if (!Array.isArray(mans)) continue;
    for (const m of mans) {
      out.push({
        instruction: m.instruction || '',
        distance_km: typeof m.length === 'number' ? m.length : 0,
        time_min: typeof m.time === 'number' ? m.time / 60 : 0,
        type: typeof m.type === 'number' ? m.type : 0,
        street: Array.isArray(m.street_names) ? m.street_names.join(', ') : '',
        begin_shape_index: typeof m.begin_shape_index === 'number' ? m.begin_shape_index : 0,
        end_shape_index: typeof m.end_shape_index === 'number' ? m.end_shape_index : 0,
      });
    }
  }
  return out;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================
function haversine([lng1, lat1], [lng2, lat2]) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) ** 2 +
            Math.cos(lat1 * Math.PI/180) * Math.cos(lat2 * Math.PI/180) *
            Math.sin(dLng/2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

function computeBearing([lng1, lat1], [lng2, lat2]) {
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δλ = (lng2 - lng1) * Math.PI / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180 / Math.PI) + 360) % 360;
}

function estimateSegmentSpeed(point, raw) {
  // Use the leg average if we don't have segment-level traffic
  const totalKm = raw.trip.summary?.length || 0;
  const totalHr = (raw.trip.summary?.time || 0) / 3600;
  return totalHr > 0 ? totalKm / totalHr : 80;
}

function simplifyPath(points, maxPoints) {
  if (points.length <= maxPoints) return points;
  const stride = Math.ceil(points.length / maxPoints);
  return points.filter((_, i) => i % stride === 0 || i === points.length - 1);
}

/**
 * Decode Valhalla polyline-6 (precision 6, vs Google polyline-5).
 * Returns array of [lng, lat] pairs.
 */
function decodePolyline6(encoded) {
  const points = [];
  let index = 0, lat = 0, lng = 0;
  while (index < encoded.length) {
    let result = 0, shift = 0, b;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = (result & 1) ? ~(result >> 1) : (result >> 1);
    lat += dlat;
    result = 0; shift = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = (result & 1) ? ~(result >> 1) : (result >> 1);
    lng += dlng;
    points.push([lng / 1e6, lat / 1e6]);
  }
  return points;
}

/**
 * Geocode a text address to {lat, lng, name, country_code}.
 *
 * `country_code` is uppercase ISO 3166-1 alpha-2 (e.g. 'IN', 'US', 'DE').
 * It enables the drive-cycle locale adjustment in the range engine
 * (see src/lib/driveCycle.js).
 *
 * Uses Nominatim by default. For production at scale we may switch to
 * Mapbox Geocoding (paid, faster) — set VITE_GEOCODER=mapbox to override.
 */
export async function geocode(address) {
  if (!address || typeof address !== 'string') return null;
  const trimmed = address.trim();
  if (!trimmed) return null;
  
  const provider = import.meta.env.VITE_GEOCODER || 'nominatim';
  
  if (provider === 'mapbox') {
    const token = import.meta.env.VITE_MAPBOX_TOKEN;
    if (!token) throw new Error('Mapbox geocoder requires VITE_MAPBOX_TOKEN');
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(trimmed)}.json?access_token=${token}&limit=1`;
    const res = await fetch(url);
    const data = await res.json();
    const f = data.features?.[0];
    if (!f) return null;
    // Mapbox returns a `context` array with parent regions. The country
    // entry has id like "country.XXX" and a `short_code` field with the
    // ISO 3166-1 alpha-2 code (lowercase). Some results put the country
    // info on the feature itself when it IS a country.
    let cc = null;
    if (Array.isArray(f.context)) {
      const country = f.context.find(c => c?.id?.startsWith('country.'));
      if (country?.short_code) cc = country.short_code.toUpperCase();
    }
    if (!cc && f.id?.startsWith('country.') && f.short_code) {
      cc = f.short_code.toUpperCase();
    }
    return {
      lat: f.center[1],
      lng: f.center[0],
      name: f.text || f.place_name,
      country_code: cc,
    };
  }
  
  // Default: Nominatim (OpenStreetMap). `addressdetails=1` adds a structured
  // `address` object that includes `country_code` (lowercase ISO 3166-1
  // alpha-2). We uppercase it to match the convention used by the engine.
  const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(trimmed)}&format=json&limit=1&addressdetails=1`;
  const res = await fetch(url, {
    headers: { 'User-Agent': 'PluginAny-TripPlanner/0.2 (community@pluginany.com)' }
  });
  const data = await res.json();
  if (!data?.[0]) return null;
  const cc = data[0].address?.country_code;
  return {
    lat: parseFloat(data[0].lat),
    lng: parseFloat(data[0].lon),
    name: data[0].display_name?.split(',')[0] || trimmed,
    country_code: cc ? cc.toUpperCase() : null,
  };
}

/**
 * Reverse-geocode a lat/lng pair to a readable place + country code.
 *
 * Used by the "Use my location" button: the browser geolocation API gives
 * raw coordinates, and we turn those into something the user recognises
 * (and the engine needs `country_code` for locale adjustment).
 *
 * Returns { lat, lng, name, country_code } — the same shape as geocode()
 * and the autocomplete selection, so callers can treat all three the same.
 * Returns null on failure.
 */
export async function reverseGeocode(lat, lng) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  try {
    const url = `https://nominatim.openstreetmap.org/reverse`
      + `?lat=${lat}&lon=${lng}&format=json&addressdetails=1&zoom=14`;
    const res = await fetch(url, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data || data.error) return null;
    const addr = data.address || {};
    // Prefer the most specific populated-place name available
    const name = addr.city || addr.town || addr.village
      || addr.suburb || addr.county
      || data.display_name?.split(',')[0]
      || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    const cc = addr.country_code ? addr.country_code.toUpperCase() : null;
    return {
      lat,
      lng,
      name,
      country_code: cc,
    };
  } catch {
    return null;
  }
}
